import { ReactElement, useEffect, useState } from 'react';
import { Box, Loader } from '@hopskipdrive/react-components';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';

import { PaginationIcon, PaginationPage } from './Pagination.styles';

interface PaginationProps {
  currentPage: number;
  pageCount: number;
  changePage: (page: number) => void;
}

const Pagination = ({ currentPage, pageCount, changePage }: PaginationProps): ReactElement => {
  const [pagesPerSection, setPagesPerSection] = useState(pageCount);
  const getCenteredPages = () => {
    if (pageCount === 0) {
      return [];
    }

    const halfToShow = Math.floor(pagesPerSection / 2);

    let start = currentPage - halfToShow;
    let end = currentPage + halfToShow + 1;

    if (start < 0) {
      end += -start;
      start = 0;
    }

    if (end > pageCount) {
      start -= end - pageCount;
      end = pageCount;
    }

    if (start < 0) {
      start = 0;
    }

    return Array.from({ length: end - start }, (_, index) => (
      <PaginationPage
        key={'page-' + (start + index + 1)}
        onClick={() => changePage(start + index + 1)}
        className={start + index + 1 === currentPage ? 'active' : ''}
      >
        {start + index + 1}
      </PaginationPage>
    ));
  };

  useEffect(() => {
    if (pageCount < 15) {
      setPagesPerSection(pageCount);
    } else {
      setPagesPerSection(15);
    }
  }, [pageCount]);

  return (
    <Box display="flex" alignItems="center">
      <PaginationIcon
        variant="text"
        disabled={currentPage === 1}
        onClick={() => changePage(currentPage - pagesPerSection > 1 ? currentPage - pagesPerSection : 1)}
      >
        <SkipPreviousIcon />
      </PaginationIcon>
      <PaginationIcon variant="text" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
        <ChevronLeftIcon />
      </PaginationIcon>

      {Number.isInteger(pageCount) ? (
        getCenteredPages()
      ) : (
        <Box padding="8px">
          <Loader size="30px" />
        </Box>
      )}

      <PaginationIcon variant="text" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)}>
        <ChevronRightIcon />
      </PaginationIcon>
      <PaginationIcon
        variant="text"
        disabled={currentPage === pageCount}
        onClick={() =>
          changePage(currentPage + pagesPerSection > pageCount ? pageCount : currentPage + pagesPerSection)
        }
      >
        <SkipNextIcon />
      </PaginationIcon>
    </Box>
  );
};

export default Pagination;
